import React from 'react';
import { computed, reaction, action } from 'mobx';

import { Variables } from 'core/query';
import { PortalLink } from 'apps/client/components/PortalLink';
import { clientStore } from 'apps/client/app';

import { PaginationOffsetManager } from 'libs/connection';
import {
  DateCell,
  makeEnumCell,
  RfqNameLink,
  ArrowButtonLink,
  CadPreview,
  Space,
} from 'libs/connection/cells';
import {
  DateFilter,
  EnumFilter,
  SpaceFilter,
} from 'libs/connection/actions/filters';
import { ValueInputType } from 'libs/connection/actions/filterModels';

import { Checkbox } from 'components';

import { analytics } from 'core/analytics';

import rfqStates from 'apps/rfq/rfqStates';

import { observer } from 'mobx-react';

import { DateWithTooltip } from 'apps/components/DateWithTooltip';

import { SpaceLink } from '../../shared';

import { RFQModel } from '../app';
import { RFQConnectionQuery, RFQIntermediatorConnectionQuery } from '../gql';

const customerSpaceCol = {
  Header: 'Customer',
  accessor: 'customerSpace',
  Cell: Space,
  filter: SpaceFilter,
  fieldType: ValueInputType.STRING,
  filterParamName: 'customerSpaceID',
};

const onCellClicked = () => {
  analytics.sendEvent('rfq-dashboard-click-view', {}, { ga: true });
};

const ReactiveCheckBox = observer(({ rfq, conn, handleChange, disabled }) => (
  <Checkbox
    id={rfq.id}
    value={rfq.id}
    onChange={handleChange}
    checked={conn.selectedRows.has(rfq)}
    disabled={disabled}
  />
));

const checkBoxColumn = {
  accessor: '',
  Cell: function preview(rfq, column, conn) {
    const handleChange = () => {
      if (conn.selectedRows.has(rfq)) {
        conn.RemoveRow(rfq);
      } else {
        conn.AddRow(rfq);
      }
    };
    return (
      <ReactiveCheckBox
        rfq={rfq}
        conn={conn}
        handleChange={handleChange}
        disabled={!rfq.isQuotable}
      />
    );
  },
};

export const columns = [
  {
    Header: 'Preview',
    accessor: '',
    Cell: function preview(rfq) {
      if (!rfq.intermediatorSession.shareFile.revision) {
        return <p>Loading...</p>;
      }
      return (
        <PortalLink
          name='rfq'
          params={{ rfqId: rfq.id }}
          openTab={{
            title: rfq.decimalId,
            iconType: 'rfq',
          }}
          onClick={onCellClicked}
        >
          {CadPreview(rfq.intermediatorSession.shareFile.revision.file)}
        </PortalLink>
      );
    },
  },
  {
    // Note: 'accessor' is overloaded - being used for attribute access but also as the key for filters/sorts.
    // We may need to change this as we add more sortable/filterable columns
    Header: 'Part name/Workspace',
    accessor: '',
    Cell: function preview(rfq) {
      return (
        <>
          <RfqNameLink
            rfq={rfq}
            onClick={onCellClicked}
            openTab={{
              title: rfq.decimalId,
              iconType: 'rfq',
            }}
          />
          <SpaceLink space={rfq.customerSpace} preferWorkspaceLink />
        </>
      );
    },
  },
  {
    Header: 'Created Date',
    isSortable: true,
    filter: DateFilter,
    accessor: 'createdAt',
    fieldType: ValueInputType.DATETIME,
    Cell: date => <DateWithTooltip date={date} />,
  },
  {
    Header: 'Status',
    isSortable: true,
    filter: EnumFilter,
    filterArgs: { valuesEnum: rfqStates },
    accessor: 'state',
    fieldType: ValueInputType.STRING,
    Cell: makeEnumCell(rfqStates),
  },
  {
    Header: 'Process',
    accessor: 'jobSpec.build.process.name',
  },
  {
    Header: 'Material',
    accessor: 'jobSpec.build.material.name',
  },
  {
    Header: 'Quantity',
    // isNumeric: true,
    accessor: 'jobSpec.quantity',
  },
  {
    accessor: '',
    Cell: function rfqLink(rfq) {
      if (!rfq.intermediatorSession.shareFile.revision) {
        return <>X</>;
      }
      return (
        <ArrowButtonLink
          name='rfq'
          params={{ rfqId: rfq.id }}
          onClick={onCellClicked}
          openTab={{
            title: rfq.decimalId,
            iconType: 'rfq',
          }}
        />
      );
    },
  },
];
export class RFQConnManager extends PaginationOffsetManager {
  static ElementModel = RFQModel;
  // TODO: move this to field definitions? - change in Orders

  static defaultOrder = 'DESC';
  static defaultSort = 'createdAt';

  constructor(parent, init, options) {
    super(parent, init, options);
    this.batchSize = 10;

    this.variables = this.variables.extend(
      new Variables({
        spaceId: () =>
          clientStore.activeSpaceId || clientStore.signedInProfile.inSpace.id,
      }),
    );

    this.variables.onChange(
      ['spaceId'],
      () => {
        this.initiateQuery();
      },
      { fireImmediately: false },
    );

    this.disposers.push(
      reaction(
        () => clientStore.currentPortal,
        () => {
          this.initiateQuery();
        },
        { fireImmediately: true },
      ),
    );
  }

  @computed get finalColumns() {
    if (clientStore.currentPortal === 'INTERMEDIATOR') {
      const cols = [checkBoxColumn, ...columns];
      cols.splice(3, 0, customerSpaceCol);

      return cols;
    }
    return columns;
  }

  @computed get filterColumns() {
    return this.finalColumns.filter(col => col.filter !== undefined);
  }

  @action initiateQuery = () => {
    this.InitialiseFilters();
    let Query = RFQConnectionQuery;
    if (clientStore.currentPortal === '') {
      return;
    }
    if (clientStore.currentPortal === 'INTERMEDIATOR') {
      Query = RFQIntermediatorConnectionQuery;
    }

    this.initialiseQuery(Query, this.variables, clientStore.roleHeaders);
  };
}
